import { NavLink, useLocation, useNavigate, useParams } from "react-router";
import { useHtzOfferShowQuery } from "@/services/backend/htz/offers/api/offer-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import t from "@/lang/lang";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import {
  OfferContextProvider,
  useOfferContext,
} from "@/routes/processes/[processId]/htz/offers/_components/offer-context";
import { OfferPdf } from "@/routes/processes/[processId]/htz/offers/[offerId]/_components/offer-pdf";
import { DeleteOfferDialog } from "@/routes/processes/[processId]/htz/offers/[offerId]/_components/delete-offer-dialog";
import { hasFieldError } from "@/shared/lib/errors/validation-error";
import {
  useHtzPositionCatalogShowActiveCatalogQuery,
  useHtzPositionCatalogShowQuery,
} from "@/services/backend/htz/position-catalog/api/position-catalog-api";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigation,
  DocumentPageNavigationLink,
  DocumentPageNavigationOutlet,
  DocumentPageSkeleton,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { DocumentImmutableAlert } from "@/shared/bsm/core/document/components/ui/document-immutable-alert";
import {
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/shared/components/ui/breadcrumb";
import { ProcessPageMainTemplate } from "@/routes/processes/[processId]/_components/process-page-main-template";
import { HtzBreadcrumbs } from "@/routes/processes/[processId]/htz/_components/htz-breadcrumbs";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import { useHtzAirHandlingUnitListQuery } from "@/services/backend/htz/ahu/api/ahu-api";
import { CreateAhuDialog } from "@/services/backend/htz/ahu/components/create-ahu-dialog";
import { Button } from "@/shared/components/ui/button";
import { Plus } from "lucide-react";
import { CpyDialog } from "@/services/backend/cpy/components/cpy-dialog";
import { CpyName } from "@/services/backend/cpy/types/entity-names";
import { v4 } from "uuid";

export function OfferRoute() {
  const { offerId } = useParams();

  const { offer, catalog, airHandlingUnits, isLoading, error } =
    useOfferRouteDependencies(offerId!);

  const breadcrumbs = offer && (
    <HtzBreadcrumbs customerId={offer.customerId} processId={offer.processId!}>
      <BreadcrumbSeparator />
      <BreadcrumbItem className="hidden md:block">
        <BreadcrumbLink asChild>
          <NavLink to={`/processes/htz/${offer.processId!}/offers`}>
            {t("Angebote")}
          </NavLink>
        </BreadcrumbLink>
      </BreadcrumbItem>
      <BreadcrumbSeparator />
      <BreadcrumbItem>
        <BreadcrumbPage>
          <DocumentNumberById documentId={offer.id} />
        </BreadcrumbPage>
      </BreadcrumbItem>
    </HtzBreadcrumbs>
  );

  return (
    <ProcessPageMainTemplate breadcrumbs={breadcrumbs}>
      {error && <RTKQueryErrorAlert error={error} />}
      {isLoading && <DocumentPageSkeleton />}
      {!isLoading && !error && offer && (
        <PersistenceContextProvider>
          <OfferContextProvider
            offer={offer}
            catalog={catalog!}
            airHandlingUnits={airHandlingUnits!}
          >
            <OfferPage />
          </OfferContextProvider>
        </PersistenceContextProvider>
      )}
    </ProcessPageMainTemplate>
  );
}

function useOfferRouteDependencies(offerId: string) {
  const {
    data: offer,
    isLoading: offerIsLoading,
    error: offerError,
  } = useHtzOfferShowQuery({
    id: offerId,
  });

  const {
    data: activeCatalog,
    isLoading: activeCatalogIsLoading,
    error: activeCatalogError,
  } = useHtzPositionCatalogShowActiveCatalogQuery(
    {
      companyId: offer?.companyId ?? "",
    },
    {
      skip: offer === undefined,
    },
  );

  const {
    data: positionCatalog,
    isLoading: catalogIsLoading,
    error: catalogError,
  } = useHtzPositionCatalogShowQuery(
    {
      id: activeCatalog?.catalogId ?? "",
    },
    {
      skip: activeCatalog === undefined || activeCatalog?.catalogId === null,
    },
  );

  const {
    data: ahuList,
    isLoading: ahuIsLoading,
    error: ahuError,
  } = useHtzAirHandlingUnitListQuery(
    {
      customer: {
        active: offer !== undefined,
        values: [offer?.customerId ?? ""],
      },
    },
    {
      skip: offer === undefined,
    },
  );
  const airHandlingUnits = ahuList?.items ?? [];

  const isLoading =
    offerIsLoading ||
    activeCatalogIsLoading ||
    catalogIsLoading ||
    ahuIsLoading;

  const error = (() => {
    switch (true) {
      case offerError !== undefined:
        return offerError;
      case activeCatalogError !== undefined:
        return activeCatalogError;
      case catalogError !== undefined:
        return catalogError;
      case ahuError !== undefined:
        return ahuError;
      case activeCatalog?.catalogId === null:
        // fake 404 error
        return {
          status: 404,
        };
      default:
        return undefined;
    }
  })();

  return {
    offer,
    catalog: positionCatalog,
    airHandlingUnits,
    isLoading,
    error,
  };
}

function OfferPage() {
  const { offer, validationError } = useOfferContext();
  const { invalidate } = useInvalidateHtzOfferRoute();

  const hasDataError = hasFieldError(validationError, "date");
  const hasPosError = hasFieldError(validationError, "positions");
  const hasAddrError = hasFieldError(
    validationError,
    "postalAddress",
    "serviceAddress",
  );
  const hasRepError = hasFieldError(validationError, "representative");

  const links = [
    {
      to: "data",
      label: t("Angebot"),
      hasValidationError: hasDataError,
    },
    {
      to: "positions",
      label: t("Leistungen"),
      hasValidationError: hasPosError,
    },
    {
      to: "addresses-and-contacts",
      label: t("Adressen & Kontakte"),
      hasValidationError: hasAddrError || hasRepError,
    },
  ];

  return (
    <DocumentPage>
      <DocumentImmutableAlert document={offer} />
      <DocumentPageHeader>
        <DocumentPageNavigation>
          {links.map((link) => (
            <DocumentPageNavigationLink
              key={link.to}
              to={link.to}
              hasValidationError={link.hasValidationError}
            >
              {link.label}
            </DocumentPageNavigationLink>
          ))}
        </DocumentPageNavigation>
        <DocumentPageActions>
          {!offer.immutable && (
            <>
              <PersistenceStateUsingContext className="mr-2" />
              <CpyDialog
                destinationEntityName={CpyName.HTZOffer}
                destinationId={offer.id}
                onCpySuccess={invalidate}
                initialParams={{
                  // Default limit to results from same customer. 90% case.
                  customer: { active: true, values: [offer.customerId] },
                  orderBy: [{ field: "updatedAt", dsc: true }],
                }}
              />
              <CreateAhuDialog
                companyId={offer.companyId}
                customerId={offer.customerId}
                serviceObjectId={offer.serviceObjectId}
                trigger={
                  <Button variant="outline">
                    <Plus />
                    {t("RLT-Anlage erstellen")}
                  </Button>
                }
              />
              <DeleteOfferDialog offerId={offer.id} />
            </>
          )}
          <OfferPdf offer={offer} />
        </DocumentPageActions>
      </DocumentPageHeader>
      <DocumentPageContent>
        <DocumentPageNavigationOutlet />
      </DocumentPageContent>
    </DocumentPage>
  );
}

// useInvalidateOfferRoute is a work-around to invalidate the
// htz offer route. The problem is that the
// useOfferRouteDependencies never re-renders and keeps
// returnung the same state. This must eventually be fixed.
// The pattern applied is wrong and does not work like this.
// Feeling stupid...
// This workaround is still better then window.location.reload()
export function useInvalidateHtzOfferRoute() {
  const navigate = useNavigate();
  const location = useLocation();

  const invalidate = () => {
    const { pathname } = location;
    const current = pathname;
    const start = 59;
    const temp = pathname.slice(0, start) + v4() + pathname.slice(start + 32);
    // we need a reliable buffer for rtk query to invalidate & refetch.
    // 1 second seemed appropriate.
    setTimeout(() => navigate(current), 1000);
    navigate(temp);
  };

  return { invalidate };
}
